import { supabase } from "@/integrations/supabase/client";

/**
 * Downloads a Stripe invoice PDF via the get-invoice-pdf edge function.
 * The PDF is fetched server-side from Stripe (pay.stripe.com is blocked on
 * some networks) and saved directly to the user's device.
 */
export async function downloadInvoicePdf(
  invoiceId: string,
  fallbackNumber?: string | null,
  options?: { voided?: boolean },
): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error("Du måste vara inloggad för att ladda ner fakturan");

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-invoice-pdf`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ invoice_id: invoiceId }),
  });

  if (!res.ok) {
    let message = `Kunde inte hämta PDF (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      // keep default message
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename="([^"]+)"/);
  const baseName = match?.[1] ?? `Faktura-${fallbackNumber ?? invoiceId}.pdf`;
  // Makulerade fakturor märks redan i filnamnet så en gammal nedladdning inte
  // förväxlas med en giltig faktura.
  const filename = options?.voided ? `MAKULERAD-${baseName}` : baseName;

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}
